import { useEffect, useState } from "react";
import { VisitedSeal } from "@/components/guide/VisitedSeal";
import { ContentImage } from "@/components/shared/ContentImage";
import { contentAssetUrl } from "@/lib/assets";
import { regionLabel } from "@/lib/content";
import {
  CRITERION_OPTIONS,
  type Community,
  type CriterionId,
  type Region,
  type UnitType,
} from "@/types/content";

import type { IntakeContactExtra } from "@/types/intake";

type CommunityCardProps = {
  community: Community;
  regions: Region[];
  rank: number;
  isTop: boolean;
  criterion: CriterionId | null;
  onIntakeContact: (extra?: IntakeContactExtra) => void;
};

export function CommunityCard({
  community,
  regions,
  rank,
  isTop,
  criterion,
  onIntakeContact,
}: CommunityCardProps) {
  const [expanded, setExpanded] = useState(isTop);

  useEffect(() => {
    setExpanded(isTop);
  }, [isTop]);

  const regionCn = regionLabel(regions, community.regionId);
  const score = criterion ? community.priorityScores[criterion] : null;
  const criterionTitle = CRITERION_OPTIONS.find((o) => o.id === criterion)?.title;

  return (
    <article
      className={`overflow-hidden rounded-xl bg-paper transition-shadow ${
        isTop
          ? "shadow-[0_12px_36px_rgba(44,87,71,0.15),0_0_0_2px_#FF6B5A]"
          : "shadow-card"
      }`}
    >
      {isTop ? (
        <div className="flex items-center justify-between bg-coral-500 px-[18px] py-2.5 text-xs font-bold text-white">
          <span className="flex items-center gap-1.5">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
              <path
                d="M6.5 0.5L7.9 4.6L12.2 4.8L8.8 7.5L9.9 11.7L6.5 9.3L3.1 11.7L4.2 7.5L0.8 4.8L5.1 4.6L6.5 0.5Z"
                fill="currentColor"
              />
            </svg>
            最符合你的选择
          </span>
          <span className="opacity-85">排名 #{rank}</span>
        </div>
      ) : null}

      <div className="relative">
        <ContentImage
          src={contentAssetUrl(community.image)}
          alt={community.nameEn}
          height={210}
          photoDim="city"
          photoLabel={community.nameEn}
          rounded={false}
          className="rounded-none"
        />
        <span className="absolute left-3.5 top-3.5 rounded-pill bg-white/90 px-2.5 py-1 text-[11.5px] font-bold text-ink-900 backdrop-blur-sm">
          {community.type}
        </span>
      </div>

      <div className="px-5 pb-1.5 pt-[18px]">
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <div>
            <h3 className="text-[19px] font-bold tracking-tight text-ink-900">
              {community.nameEn}
            </h3>
            <p className="mt-0.5 text-xs text-ink-500">{regionCn}</p>
          </div>
          {!isTop ? (
            <span className="font-mono text-[11px] font-bold text-ink-400">#{rank}</span>
          ) : null}
        </div>

        <p className="text-flow mb-4 text-[13.5px] leading-relaxed text-ink-600">
          {community.description}
        </p>

        {community.visited ? <VisitedSeal note={community.visitNote} /> : null}

        {criterion && score !== null && criterionTitle ? (
          <div className="mt-4 flex flex-col gap-2 text-xs font-medium text-ink-600">
            <span>{criterionTitle}</span>
            <div className="flex items-center gap-2.5">
              <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-coral-500 to-coral-700 transition-[width] duration-300"
                  style={{ width: `${score * 10}%` }}
                />
              </div>
              <span className="shrink-0 font-bold text-coral-700">{score}/10</span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="px-5 pb-[18px]">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex w-full items-center justify-between border-t border-[var(--line)] py-4 text-[13.5px] font-semibold text-ink-900"
        >
          <span>查看 {community.unitTypes.length} 种户型</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-hidden
          >
            <path
              d="M3 5l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {expanded ? (
          <div className="flex flex-col gap-2.5">
            {community.unitTypes.map((u) => (
              <UnitRow
                key={u.id}
                unit={u}
                onIntake={() =>
                  onIntakeContact({
                    communityId: community.id,
                    communityNameEn: community.nameEn,
                    unitId: u.id,
                    source: "unit",
                  })
                }
              />
            ))}
            <div className="mt-1 flex items-center justify-between gap-3 rounded-2xl border border-coral-100 bg-coral-50 px-4 py-3.5">
              <p className="min-w-0 flex-1 text-[12.5px] leading-snug text-ink-700">
                想知道这个小区
                <strong className="font-bold text-coral-900">现在有没有合适的房?</strong>
              </p>
              <button
                type="button"
                onClick={() =>
                  onIntakeContact({
                    communityId: community.id,
                    communityNameEn: community.nameEn,
                    source: "community",
                  })
                }
                className="shrink-0 rounded-pill bg-coral-700 px-4 py-2 text-[13px] font-semibold text-white shadow-[0_4px_12px_rgba(213,84,56,0.3)]"
              >
                让管家看看
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
}

function UnitRow({ unit, onIntake }: { unit: UnitType; onIntake: () => void }) {
  return (
    <div className="rounded-md border border-[var(--line-soft)] bg-paper-warm p-3.5">
      <div className="flex gap-3">
        <ContentImage
          src={contentAssetUrl(unit.floorPlanImage)}
          alt={unit.name}
          height={64}
          photoDim="floor"
          photoLabel={unit.name}
          className="h-16 w-16 shrink-0 rounded-md"
          rounded
        />
        <div className="min-w-0 flex-1">
          <div>
            <p className="text-sm font-medium text-ink-900">{unit.name}</p>
            <p className="mt-0.5 text-[11px] text-ink-400">{unit.area}</p>
          </div>
          <p className="mt-1 font-serif text-base font-medium text-forest-900">
            {unit.priceText}
          </p>
          <p className="mt-1.5 flex items-start gap-1 text-[11px] italic leading-snug text-gold-700">
            <span className="mt-px shrink-0">ⓘ</span>
            {unit.priceJudgment}
          </p>
        </div>
        <button
          type="button"
          onClick={onIntake}
          className="flex h-7 w-7 shrink-0 items-center justify-center self-start rounded-full border border-ink-200 bg-surface-50 text-forest-900"
          aria-label="问管家"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path
              d="M2 3.5h10v6H5l-3 2.5v-8.5z"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
